import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Chip,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import StarIcon from '@mui/icons-material/Star';
import {
    LYRICS_SCRIPTS,
    createSongLyrics,
    deleteSongLyrics,
    listSongLyrics,
    scriptLabel,
    updateSongLyrics,
} from '../../../../_services/creater/songContent.service';

const BLANK = {
    script: 'latin',
    language: '',
    content: '',
    credit: '',
    is_translation: false,
    is_default: false,
};

/**
 * The lyrics variants of one song.
 *
 * A song can carry several: a Devanagari original, a romanized transliteration
 * and an English translation are three rows, not three songs, and exactly one of
 * them is the default the public page opens on. The backend enforces that
 * one-default rule on every write — including promoting a successor when the
 * default is deleted — so this screen never computes it, it just re-reads the
 * list afterwards and shows what came back.
 *
 * The uniqueness key is (song, language, script), which is why the editor lets
 * both be changed on an existing row: two variants that collide on that pair are
 * rejected by the database, and the message says so.
 */
export default function SongLyricsEditor({ songId, languageList, disabled }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');

    // The row being edited, or BLANK for a new one. `editingId` is null while
    // adding — the form is the same either way, only the verb differs.
    const [editingId, setEditingId] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(BLANK);

    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const load = React.useCallback(async () => {
        if (!songId) return;
        setLoading(true);
        const response = await listSongLyrics(songId);
        if (!mounted.current) return;
        setRows(((response && response.data && response.data.data) || []));
        setLoading(false);
    }, [songId]);

    useEffect(() => { load(); }, [load]);

    const startAdd = () => {
        setEditingId(null);
        // The first variant of a song is its default. Any later one is not,
        // because promoting it would silently demote the one already published.
        setForm({ ...BLANK, is_default: rows.length === 0 });
        setOpen(true);
        setError('');
        setNotice('');
    };

    const startEdit = (row) => {
        setEditingId(row.id);
        setForm({
            script: row.script || 'latin',
            language: row.language || '',
            content: row.content || '',
            credit: row.credit || '',
            is_translation: Boolean(row.is_translation),
            is_default: Boolean(row.is_default),
        });
        setOpen(true);
        setError('');
        setNotice('');
    };

    const cancel = () => {
        setOpen(false);
        setEditingId(null);
        setForm(BLANK);
        setError('');
    };

    const failureText = (response) => {
        const data = (response && response.data) || {};
        // A collision on (song, language, script) surfaces as a non_field_errors
        // list. Naming the actual clash is more use than "could not be saved".
        const nonField = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : null;
        const fieldError = Object.keys(data)
            .filter((key) => Array.isArray(data[key]))
            .map((key) => data[key][0])[0];
        return nonField
            || data.message
            || fieldError
            || 'Those lyrics could not be saved.';
    };

    const submit = async () => {
        if (!String(form.content || '').trim()) {
            setError('Add the lyrics themselves before saving.');
            return;
        }
        setSaving(true);
        setError('');
        setNotice('');

        // `language` is a nullable FK: '' would be sent as the string "" and
        // rejected, so an unset language goes as null.
        const payload = {
            ...form,
            language: form.language === '' ? null : form.language,
            song: songId,
        };

        const response = editingId
            ? await updateSongLyrics(editingId, payload)
            : await createSongLyrics(payload);
        if (!mounted.current) return;
        setSaving(false);

        if (response && (response.status === 200 || response.status === 201)) {
            setNotice(editingId ? 'Lyrics updated.' : 'Lyrics added.');
            cancel();
            // Re-read rather than patching state: saving a default demotes a
            // sibling server-side, and only a fresh list shows that.
            load();
            return;
        }
        setError(failureText(response));
    };

    const remove = async (row) => {
        setSaving(true);
        setError('');
        setNotice('');
        const response = await deleteSongLyrics(row.id);
        if (!mounted.current) return;
        setSaving(false);
        if (response && (response.status === 204 || response.status === 200)) {
            setNotice('Lyrics removed.');
            load();
            return;
        }
        setError(failureText(response));
    };

    return (
        <div>
            <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>Lyrics</Typography>
                <button
                    type="button"
                    className="gn-actionbtn outlined dark"
                    style={{ padding: '4px 12px', fontSize: '0.75rem' }}
                    disabled={disabled || saving || open}
                    onClick={startAdd}
                >
                    + ADD VARIANT
                </button>
            </div>
            <Typography sx={{ fontSize: 12, color: '#6B7280', mb: 2 }}>
                One row per script or language — an original, a romanized version and a
                translation are three variants of the same song. The one marked default is
                what the song page opens on.
            </Typography>

            {loading && <Skeleton variant="rounded" height={64} />}

            {!loading && rows.length === 0 && !open && (
                <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>
                    No lyrics yet.
                </Typography>
            )}

            {!loading && rows.map((row) => (
                <div
                    key={row.id}
                    className="d-flex align-items-start justify-content-between border rounded p-2 mb-2"
                >
                    <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                            <Chip
                                size="small"
                                label={scriptLabel(row.script)}
                                sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }}
                            />
                            {row.language_name && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={row.language_name}
                                    sx={{ height: 20, fontSize: 10.5 }}
                                />
                            )}
                            {row.is_translation && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label="Translation"
                                    sx={{ height: 20, fontSize: 10.5 }}
                                />
                            )}
                            {row.is_default && (
                                <Tooltip title="Shown first on the song page" arrow>
                                    <Chip
                                        size="small"
                                        icon={<StarIcon sx={{ fontSize: 12 }} />}
                                        label="Default"
                                        color="primary"
                                        sx={{ height: 20, fontSize: 10.5, fontWeight: 700 }}
                                    />
                                </Tooltip>
                            )}
                        </div>
                        <Typography
                            sx={{
                                fontSize: 12.5,
                                color: '#3A4256',
                                whiteSpace: 'pre-wrap',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                            }}
                        >
                            {row.content}
                        </Typography>
                        {row.credit && (
                            <Typography sx={{ fontSize: 11, color: '#9AA1AE', mt: 0.5 }}>
                                {row.credit}
                            </Typography>
                        )}
                    </div>
                    <div className="d-flex flex-shrink-0">
                        <button
                            type="button"
                            title="Edit these lyrics"
                            className="gn-btn-icononly text-secondary"
                            disabled={disabled || saving}
                            onClick={() => startEdit(row)}
                        >
                            <EditOutlinedIcon fontSize="small" />
                        </button>
                        <button
                            type="button"
                            title="Remove these lyrics"
                            className="gn-btn-icononly text-secondary"
                            disabled={disabled || saving}
                            onClick={() => remove(row)}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </button>
                    </div>
                </div>
            ))}

            {open && (
                <div className="border rounded p-3 mt-2">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={disabled || saving}>
                                <InputLabel id={`lyrics_script_${songId}`}>Script</InputLabel>
                                <Select
                                    labelId={`lyrics_script_${songId}`}
                                    label="Script"
                                    value={form.script}
                                    onChange={(event) => setForm((p) => ({ ...p, script: event.target.value }))}
                                >
                                    {LYRICS_SCRIPTS.map((script) => (
                                        <MenuItem key={script} value={script}>
                                            {scriptLabel(script)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small" disabled={disabled || saving}>
                                <InputLabel id={`lyrics_language_${songId}`}>Language</InputLabel>
                                <Select
                                    labelId={`lyrics_language_${songId}`}
                                    label="Language"
                                    value={form.language}
                                    onChange={(event) => setForm((p) => ({ ...p, language: event.target.value }))}
                                >
                                    <MenuItem value="">
                                        <em>Not set</em>
                                    </MenuItem>
                                    {(languageList || []).map((item) => (
                                        <MenuItem key={item.id} value={item.id}>
                                            {item.language}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                className="w-100"
                                size="small"
                                label="Lyrics"
                                multiline
                                rows={6}
                                value={form.content}
                                onChange={(event) => setForm((p) => ({ ...p, content: event.target.value }))}
                                disabled={disabled || saving}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                className="w-100"
                                size="small"
                                label="Credit"
                                value={form.credit}
                                onChange={(event) => setForm((p) => ({ ...p, credit: event.target.value }))}
                                disabled={disabled || saving}
                                helperText="Who wrote or translated this version."
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.is_translation}
                                        onChange={(event) => setForm((p) => ({ ...p, is_translation: event.target.checked }))}
                                        disabled={disabled || saving}
                                    />
                                }
                                label={<span style={{ fontSize: 13 }}>This is a translation</span>}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.is_default}
                                        onChange={(event) => setForm((p) => ({ ...p, is_default: event.target.checked }))}
                                        disabled={disabled || saving}
                                    />
                                }
                                label={<span style={{ fontSize: 13 }}>Show this one first</span>}
                            />
                        </Grid>
                    </Grid>

                    {error && (
                        <Alert severity="warning" className="mt-2" sx={{ fontSize: 13, py: 0.25 }}>
                            {error}
                        </Alert>
                    )}

                    <div className="mt-3 d-flex align-items-center gap-3 flex-wrap">
                        <button
                            type="button"
                            className="gn-actionbtn"
                            style={{ padding: '6px 16px', fontSize: '0.8rem' }}
                            disabled={disabled || saving}
                            onClick={submit}
                        >
                            {saving ? 'Saving…' : (editingId ? 'Update lyrics' : 'Add lyrics')}
                        </button>
                        <button
                            type="button"
                            className="gn-actionbtn outlined dark"
                            style={{ padding: '6px 16px', fontSize: '0.8rem' }}
                            disabled={saving}
                            onClick={cancel}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {!open && error && (
                <Alert severity="warning" className="mt-2" sx={{ fontSize: 13, py: 0.25 }}>
                    {error}
                </Alert>
            )}
            {!open && notice && (
                <Alert severity="success" className="mt-2" sx={{ fontSize: 13, py: 0.25 }}>
                    {notice}
                </Alert>
            )}
        </div>
    );
}
